import axios from 'axios';
import { BACKEND_URL } from './backend';

const API_BASE = BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear and redirect if we're not already on the auth page
      if (!window.location.pathname.includes('/auth')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  resetPasswordWithCode: (data) => api.post('/auth/reset-password-with-code', data),
};

// Users
export const usersAPI = {
  // Accepts either a plain role string ('customer') OR an object
  // ({ role: 'customer' }). Several call sites had drifted to the object
  // form which silently sent `role=[object Object]` and returned the full
  // user list (leaking agents/admins into customer dropdowns). This
  // normaliser keeps existing callers working regardless of shape.
  list: (roleOrParams) => {
    const params = typeof roleOrParams === 'object' && roleOrParams !== null
      ? roleOrParams
      : { role: roleOrParams };
    return api.get('/users', { params });
  },
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/auth/register', data),
  delete: (id) => api.delete(`/users/${id}`),
  bulkDelete: (user_ids) => api.post('/users/bulk-delete', user_ids),
};

// Departments
export const departmentsAPI = {
  list: () => api.get('/departments'),
  create: (data) => api.post('/departments', data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Tickets
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  counts: () => api.get('/tickets/counts'),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  bulkDelete: (ticket_ids) => api.post('/tickets/bulk-delete', ticket_ids),
  getMessages: (id) => api.get(`/tickets/${id}/messages`),
  addMessage: (id, data) => api.post(`/tickets/${id}/messages`, data),
  // Work order queue + tier management
  getQueue: () => api.get('/tickets/queue'),
  claim: (id) => api.post(`/tickets/${id}/claim`, {}),
  requestElevation: (id, payload) => {
    // Backwards compatible: accept a string `reason` OR a full payload object
    const body = typeof payload === 'string' ? { reason: payload } : (payload || {});
    return api.post(`/tickets/${id}/request-elevation`, body);
  },
  getCalendar: (params) => api.get('/tickets/calendar/scheduled', { params }),
};

// Knowledge Base
export const knowledgeAPI = {
  list: (params) => api.get('/knowledge', { params }),
  get: (id) => api.get(`/knowledge/${id}`),
  create: (data) => api.post('/knowledge', data),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  delete: (id) => api.delete(`/knowledge/${id}`),
  getCategories: () => api.get('/knowledge/categories'),
};

// Category management (admin-managed; type = 'ticket' | 'kb' | 'documentation')
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.patch(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Sites — physical locations under a customer (each customer can have many).
export const sitesAPI = {
  list: (params) => api.get('/sites', { params }),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.patch(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
};

// Calendar events (separate from scheduled tickets)
export const calendarAPI = {
  listEvents: (params) => api.get('/calendar/events', { params }),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data) => api.patch(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
};

// Email settings (admin-only) — Zepto Mail
export const emailSettingsAPI = {
  get: () => api.get('/settings/email'),
  update: (data) => api.patch('/settings/email', data),
  test: (to) => api.post('/settings/email/test', { to }),
  logs: (limit = 50) => api.get('/settings/email/logs', { params: { limit } }),
};

// Company settings — appears on PDFs (logo + address) and outgoing emails
export const companySettingsAPI = {
  get: () => api.get('/settings/company'),
  update: (data) => api.put('/settings/company', data),
};

// AI
export const aiAPI = {
  suggestReply: (ticketId, context) => api.post('/ai/suggest-reply', { ticket_id: ticketId, context }),
  summarize: (ticketId) => api.post('/ai/summarize', { ticket_id: ticketId }),
  chatbot: (question, sessionId) => api.post('/ai/chatbot', { question, session_id: sessionId }),
};

// Analytics
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getAgentStats: (agentId) => api.get(`/analytics/agent/${agentId}`),
};

// Chat
export const chatAPI = {
  getSessions: () => api.get('/chat/sessions'),
  getMessages: (sessionId) => api.get(`/chat/sessions/${sessionId}/messages`),
};

// Inventory
export const inventoryAPI = {
  // Items
  listItems: (params) => api.get('/inventory/items', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.patch(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  
  // Transactions
  createTransaction: (data) => api.post('/inventory/transactions', data),
  listTransactions: (params) => api.get('/inventory/transactions', { params }),
  
  // Utilities
  getLowStock: () => api.get('/inventory/low-stock'),
  getCategories: () => api.get('/inventory/categories'),
  getWarehouses: () => api.get('/inventory/warehouses'),
};

// HR Portal — employee self-service + admin HR management
export const hrAPI = {
  getProfile: () => api.get('/hr/profile'),
  updateProfile: (data) => api.patch('/hr/profile', data),
  listEmployees: () => api.get('/hr/employees'),
  listDocuments: (params) => api.get('/hr/documents', { params }),
  uploadDocument: (formData) => api.post('/hr/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadDocument: (id) => api.get(`/hr/documents/${id}/download`, { responseType: 'blob' }),
  deleteDocument: (id) => api.delete(`/hr/documents/${id}`),
  myPtoBalance: () => api.get('/hr/pto/balance'),
  allPtoBalances: () => api.get('/hr/pto/balances'),
  setPtoBalance: (employeeId, data) => api.put(`/hr/pto/balances/${employeeId}`, data),
  createPtoRequest: (data) => api.post('/hr/pto/requests', data),
  listPtoRequests: (params) => api.get('/hr/pto/requests', { params }),
  reviewPtoRequest: (id, data) => api.patch(`/hr/pto/requests/${id}`, data),
  cancelPtoRequest: (id) => api.delete(`/hr/pto/requests/${id}`),
  teamPtoCalendar: (params) => api.get('/hr/pto/team-calendar', { params }),
  sendWeeklyReport: () => api.post('/hr/reports/weekly-hours/send'),
  getBenefits: () => api.get('/hr/benefits'),
  updateBenefits: (data) => api.put('/hr/benefits', data),
  createTask: (data) => api.post('/hr/tasks', data),
  listTasks: (params) => api.get('/hr/tasks', { params }),
  completeTask: (id) => api.patch(`/hr/tasks/${id}/complete`),
  deleteTask: (id) => api.delete(`/hr/tasks/${id}`),
  createRequest: (data) => api.post('/hr/requests', data),
  listRequests: (params) => api.get('/hr/requests', { params }),
  replyRequest: (id, message) => api.post(`/hr/requests/${id}/reply`, { message }),
  setRequestStatus: (id, status) => api.patch(`/hr/requests/${id}`, { status }),
  myTimesheet: () => api.get('/hr/my-timesheet'),
  weekSchedule: (params) => api.get('/hr/schedule', { params }),
  listShiftDefs: (params) => api.get('/hr/schedule/shifts', { params }),
  createShift: (data) => api.post('/hr/schedule/shifts', data),
  updateShift: (id, data) => api.patch(`/hr/schedule/shifts/${id}`, data),
  deleteShift: (id) => api.delete(`/hr/schedule/shifts/${id}`),
  copyWeek: (data) => api.post('/hr/schedule/copy-week', data),
};

// RMM Phase 1 — device monitoring & management
export const rmmAPI = {
  summary: () => api.get('/rmm/summary'),
  listDevices: (params) => api.get('/rmm/devices', { params }),
  getDevice: (id) => api.get(`/rmm/devices/${id}`),
  deviceMetrics: (id, hours = 24) => api.get(`/rmm/devices/${id}/metrics`, { params: { hours } }),
  revokeDeviceToken: (id) => api.post(`/rmm/devices/${id}/revoke-token`),
  deleteDevice: (id) => api.delete(`/rmm/devices/${id}`),
  listKeys: () => api.get('/rmm/enrollment-keys'),
  createKey: (data) => api.post('/rmm/enrollment-keys', data),
  revokeKey: (id) => api.patch(`/rmm/enrollment-keys/${id}/revoke`),
  assignTechnician: (deviceId, technicianId) => api.patch(`/rmm/devices/${deviceId}/assign`, { technician_id: technicianId }),
  listCommands: (deviceId) => api.get(`/rmm/devices/${deviceId}/commands`),
  queueCommand: (deviceId, type, params = {}) => api.post(`/rmm/devices/${deviceId}/commands`, { type, params }),
  alertToTicket: (alertId) => api.post(`/rmm/alerts/${alertId}/create-ticket`),
  // Phases 3 & 4
  listPolicies: () => api.get('/rmm/policies'),
  createPolicy: (d) => api.post('/rmm/policies', d),
  deletePolicy: (id) => api.delete(`/rmm/policies/${id}`),
  clonePolicy: (id) => api.post(`/rmm/policies/${id}/clone`),
  applyTemplate: (name) => api.post(`/rmm/policies/templates/${encodeURIComponent(name)}`),
  listTemplates: () => api.get('/rmm/policies/templates'),
  simulatePolicy: (id, deviceId) => api.post(`/rmm/policies/${id}/simulate?device_id=${deviceId}`),
  effectivePolicy: (deviceId) => api.get(`/rmm/devices/${deviceId}/effective-policy`),
  listGroups: () => api.get('/rmm/groups'),
  createGroup: (d) => api.post('/rmm/groups', d),
  deleteGroup: (id) => api.delete(`/rmm/groups/${id}`),
  listMaintenance: () => api.get('/rmm/maintenance'),
  createMaintenance: (d) => api.post('/rmm/maintenance', d),
  endMaintenance: (id) => api.patch(`/rmm/maintenance/${id}/end`),
  healthSummary: () => api.get('/rmm/health'),
  listRules: () => api.get('/rmm/automation/rules'),
  createRule: (d) => api.post('/rmm/automation/rules', d),
  toggleRule: (id) => api.patch(`/rmm/automation/rules/${id}/toggle`),
  deleteRule: (id) => api.delete(`/rmm/automation/rules/${id}`),
  automationHistory: () => api.get('/rmm/automation/history'),
  decideAutomation: (id, decision) => api.post(`/rmm/automation/history/${id}/${decision}`),
  listScripts: () => api.get('/rmm/scripts'),
  createScript: (d) => api.post('/rmm/scripts', d),
  setScriptStatus: (id, status) => api.patch(`/rmm/scripts/${id}/status?status=${status}`),
  deleteScript: (id) => api.delete(`/rmm/scripts/${id}`),
  agentScript: (id) => api.get(`/rmm/enrollment-keys/${id}/agent-script`, { params: { base: BACKEND_URL }, responseType: 'blob' }),
  agentExe: (id) => api.get(`/rmm/enrollment-keys/${id}/agent-exe`, { params: { base: BACKEND_URL }, responseType: 'blob' }),
  signingCertInfo: () => api.get('/rmm/signing-cert/info'),
  uploadSigningCert: (formData) => api.post('/rmm/signing-cert', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  resignBinaries: () => api.post('/rmm/signing-cert/resign'),
  removeSigningCert: () => api.delete('/rmm/signing-cert'),
  agentSigningCert: () => api.get('/rmm/agent-signing-cert', { responseType: 'blob' }),
  listAlerts: (params) => api.get('/rmm/alerts', { params }),
  actOnAlert: (id, action) => api.patch(`/rmm/alerts/${id}`, { action }),
  auditLogs: (limit = 100) => api.get('/rmm/audit-logs', { params: { limit } }),
  // Phase 5 — network & server monitoring
  netDevices: (params) => api.get('/rmm/network/devices', { params }),
  createNetDevice: (d) => api.post('/rmm/network/devices', d),
  updateNetDevice: (id, d) => api.patch(`/rmm/network/devices/${id}`, d),
  netDeviceMetrics: (id, hours = 24) => api.get(`/rmm/network/devices/${id}/metrics`, { params: { hours } }),
  snmpProfiles: () => api.get('/rmm/network/snmp-profiles'),
  createSnmpProfile: (d) => api.post('/rmm/network/snmp-profiles', d),
  deleteSnmpProfile: (id) => api.delete(`/rmm/network/snmp-profiles/${id}`),
  discoveryJobs: () => api.get('/rmm/network/discovery'),
  startDiscovery: (d) => api.post('/rmm/network/discovery', d),
  enrollDiscovered: (jobId, ips) => api.post(`/rmm/network/discovery/${jobId}/enroll`, { ips }),
  websites: () => api.get('/rmm/websites'),
  createWebsite: (d) => api.post('/rmm/websites', d),
  toggleWebsite: (id) => api.patch(`/rmm/websites/${id}/toggle`),
  deleteWebsite: (id) => api.delete(`/rmm/websites/${id}`),
  websiteMetrics: (id, hours = 24) => api.get(`/rmm/websites/${id}/metrics`, { params: { hours } }),
  topology: (siteId) => api.get('/rmm/topology', { params: { site_id: siteId } }),
  infraSummary: () => api.get('/rmm/infrastructure/summary'),
  setMonitoredServices: (deviceId, services) => api.patch(`/rmm/devices/${deviceId}/monitored-services`, { services }),
  // Phase 6 — backup, security, M365
  backupSummary: () => api.get('/rmm/backup/summary'),
  backupJobs: (params) => api.get('/rmm/backup/jobs', { params }),
  backupPolicies: () => api.get('/rmm/backup/policies'),
  createBackupPolicy: (d) => api.post('/rmm/backup/policies', d),
  deleteBackupPolicy: (id) => api.delete(`/rmm/backup/policies/${id}`),
  backupSources: () => api.get('/rmm/backup/sources'),
  createBackupSource: (d) => api.post('/rmm/backup/sources', d),
  m365Tenants: () => api.get('/rmm/m365/tenants'),
  createM365Tenant: (d) => api.post('/rmm/m365/tenants', d),
  checkM365Tenant: (id) => api.post(`/rmm/m365/tenants/${id}/check`),
  deleteM365Tenant: (id) => api.delete(`/rmm/m365/tenants/${id}`),
  securityPosture: () => api.get('/rmm/security/posture'),
  scoreWeights: () => api.get('/rmm/security/weights'),
  setScoreWeights: (weights) => api.put('/rmm/security/weights', { weights }),
  // Phase 7 — remote management
  startSession: (deviceId, reason) => api.post(`/rmm/devices/${deviceId}/sessions`, { reason }),
  sessionExec: (sessionId, command_text, shell = 'powershell') => api.post(`/rmm/sessions/${sessionId}/exec`, { command_text, shell }),
  sessionTranscript: (sessionId) => api.get(`/rmm/sessions/${sessionId}`),
  endSession: (sessionId) => api.post(`/rmm/sessions/${sessionId}/end`),
  listSessions: (params) => api.get('/rmm/sessions', { params }),
  winTool: (deviceId, tool, params = {}) => api.post(`/rmm/devices/${deviceId}/wintool`, { tool, params }),
  registryOp: (deviceId, d) => api.post(`/rmm/devices/${deviceId}/registry`, d),
  fileOp: (deviceId, d) => api.post(`/rmm/devices/${deviceId}/files`, d),
  softwareAction: (deviceId, action, pkg) => api.post(`/rmm/devices/${deviceId}/software`, { action, package: pkg }),
  outdatedSoftware: () => api.get('/rmm/software/outdated'),
  createBulk: (d) => api.post('/rmm/bulk', d),
  listBulk: () => api.get('/rmm/bulk'),
  bulkDetail: (id) => api.get(`/rmm/bulk/${id}`),
  cancelBulk: (id) => api.post(`/rmm/bulk/${id}/cancel`),
  listSchedules: () => api.get('/rmm/schedules'),
  createSchedule: (d) => api.post('/rmm/schedules', d),
  deleteSchedule: (id) => api.delete(`/rmm/schedules/${id}`),
  deviceTickets: (deviceId) => api.get(`/rmm/devices/${deviceId}/tickets`),
  getCommand: (deviceId) => api.get(`/rmm/devices/${deviceId}/commands`),
  // Phase 8 — MSP ops & AI
  mspSummary: () => api.get('/rmm/msp/summary'),
  customerHealth: () => api.get('/rmm/msp/customer-health'),
  techOps: (days = 30) => api.get('/rmm/msp/tech-ops', { params: { days } }),
  listAssets: (params) => api.get('/rmm/assets', { params }),
  setLifecycle: (deviceId, d) => api.patch(`/rmm/devices/${deviceId}/lifecycle`, d),
  customerReport: (customerId, days = 30) => api.get(`/rmm/msp/reports/customer/${customerId}`, { params: { days } }),
  customerReportPdf: (customerId, days = 30) => api.get(`/rmm/msp/reports/customer/${customerId}`, { params: { days, format: 'pdf' }, responseType: 'blob' }),
  executiveReportPdf: () => api.get('/rmm/msp/reports/executive', { params: { format: 'pdf' }, responseType: 'blob' }),
  alertKb: (alertId) => api.get(`/rmm/alerts/${alertId}/kb`),
  aiAlertSummary: (alertId) => api.post('/rmm/ai/alert-summary', { alert_id: alertId }),
  aiDeviceAssist: (deviceId) => api.post('/rmm/ai/device-assist', { device_id: deviceId }),
  aiTicketAssist: (ticketId) => api.post('/rmm/ai/ticket-assist', { ticket_id: ticketId }),
  globalSearch: (q) => api.get('/rmm/search', { params: { q } }),
  // Phase 12 — remote desktop / control
  startRemote: (deviceId, opts = {}) => api.post('/rmm/remote/sessions', { device_id: deviceId, ...opts }),
  endRemote: (sid) => api.post(`/rmm/remote/sessions/${sid}/end`),
  remoteSessions: (params) => api.get('/rmm/remote/sessions', { params }),
  remoteSession: (sid) => api.get(`/rmm/remote/sessions/${sid}`),
  remoteSummary: (sid, summary) => api.post(`/rmm/remote/sessions/${sid}/summary`, { summary }),
  remotePolicies: () => api.get('/rmm/remote/policies'),
  upsertRemotePolicy: (d) => api.post('/rmm/remote/policies', d),
  deleteRemotePolicy: (id) => api.delete(`/rmm/remote/policies/${id}`),
  deviceRemotePolicy: (deviceId) => api.get(`/rmm/remote/devices/${deviceId}/policy`),
  // Session recordings (encrypted at rest)
  remoteRecordings: (deviceId) => api.get('/rmm/remote/recordings', { params: deviceId ? { device_id: deviceId } : {} }),
  remoteRecordingManifest: (rid) => api.get(`/rmm/remote/recordings/${rid}/manifest`),
  remoteRecordingFrame: (rid, idx) => api.get(`/rmm/remote/recordings/${rid}/frame/${idx}`, { responseType: 'blob' }),
  deleteRemoteRecording: (rid) => api.delete(`/rmm/remote/recordings/${rid}`),
  // Agent tooling downloads
  agentUninstallerExe: () => api.get('/rmm/agent-uninstaller-exe', { responseType: 'blob' }),
  agentCaptureHelperExe: () => api.get('/rmm/agent-capture-helper-exe', { responseType: 'blob' }),
};

export default api;
